import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { connect } from 'react-redux';
import queryString from 'query-string';
import { bindActionCreators } from 'redux';
import { FormattedMessage } from 'react-intl';
import { getBreadcrumbsListData } from 'shared/helpers/advert';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { setNextSearchItemIndex } from 'shared/actions/history';
import { isLocationCountry } from 'shared/helpers/transportInfo';
import { generateSearchUrl, generateSearchQuery } from 'shared/helpers/urls';
import JsonLdApplication from 'shared/components/JsonLdApplication/JsonLdApplication';
import rightArrow from 'shared/assets/images/right-arrow-line.svg';
import leftArrow from 'shared/assets/images/left-arrow-line.svg';
import greyArrow from 'shared/assets/images/angle-gray.svg';
import LINKS from 'desktop/constants/navigation';
import Link from 'shared/components/Link';
import s from './SearchBar.css';

const PER_PAGE = 20;

class SearchBar extends React.Component {
  static propTypes = {
    searchData: PropTypes.shape().isRequired,
    searchFormState: PropTypes.shape().isRequired,
    nextItemLink: PropTypes.string,
    setNextSearchItemIndex: PropTypes.func.isRequired,
    nextSearchItemIndex: PropTypes.number,
    locale: PropTypes.string,
  };
  static defaultProps = {
    nextItemLink: null,
    nextSearchItemIndex: null,
    locale: '',
    title: '',
  };

  getCorrectTransport = transport => {
    if (!transport) {
      return null;
    } else if (LINKS.SEARCH[transport.slug]) {
      return transport;
    } else if (
      transport.parents &&
      transport.parents.length > 0 &&
      LINKS.SEARCH[transport.parents[0].slug]
    ) {
      return transport.parents[0];
    }

    return null;
  };

  handleNextAdvertClick = () => {
    this.props.setNextSearchItemIndex(this.props.nextSearchItemIndex + 1);
  };

  render() {
    const { searchData, nextItemLink, searchFormState, nextSearchItemIndex } = this.props;
    const { make, model, transport, location, year, isNew } = searchData;
    const correctTransport = this.getCorrectTransport(transport);
    const q = { transport: correctTransport, make };
    const rootPath = '/';
    const firstLevelLink = `/${generateSearchUrl({ transport: correctTransport, isNew })}`;
    const makeLink = `/${generateSearchUrl({ ...q, make, transport: correctTransport, isNew })}`;
    const modelLink = `/${generateSearchUrl({ ...q, model, transport: correctTransport, isNew })}`;
    const isCountry = isLocationCountry(location);
    const yearLink = `/${generateSearchUrl({
      ...q,
      model,
      year,
      transport: correctTransport,
      isNew,
    })}`;
    const locationLink = `/${generateSearchUrl({
      ...q,
      model,
      transport: correctTransport,
      isNew,
      ...(transport && transport.slug === 'avto' && !isCountry ? { year } : {}),
      location,
    })}`;

    // Back
    const page = nextSearchItemIndex ? Math.ceil((nextSearchItemIndex - 1) / PER_PAGE) : null;
    const backUrl = page
      ? `/${generateSearchUrl(searchFormState)}?${queryString.stringify(
          generateSearchQuery({ ...searchFormState, page }),
        )}`
      : null;

    const breadcrumbLinks = {
      transport: firstLevelLink,
      make: makeLink,
      model: modelLink,
      year: yearLink,
      location: locationLink,
    };

    const breadcrumbsListData = getBreadcrumbsListData(searchData, breadcrumbLinks);

    let transportTitle = '';
    if (transport.slug === 'avto') {
      transportTitle = isNew ? 'Новые авто' : 'БУ авто';
    } else if (correctTransport) {
      transportTitle = correctTransport.title;
    }

    return (
      <div className={s.root}>
        <div className={s.flexRow}>
          <div className={cx(s.left, 'breadcrumb')}>
            {backUrl &&
              <li
                className={s.breadcrumbsItem}
                itemScope
                itemType="https://schema.org/ListItem"
                itemProp="itemListElement"
              >
                <Link to={backUrl} className={cx(s.back, 'meta-title-link')} itemProp="url">
                  <img src={leftArrow} width={12} alt="" />
                  &nbsp;
                  <FormattedMessage id="В результаты поиска" />
                </Link>
                <img className={s.divider} src={greyArrow} width="14" height="40" alt="" />
              </li>}

            {!backUrl &&
              <li
                className={s.breadcrumbsItem}
                itemScope
                itemType="https://schema.org/ListItem"
                itemProp="itemListElement"
              >
                <Link
                  to={rootPath}
                  className={cx(s.select, s.firstLetterUppercase, 'meta-title-link')}
                  itemProp="url"
                >
                  <span itemProp="title">
                    <FormattedMessage id={'Автобазар'} />
                  </span>
                </Link>
                <img className={s.divider} src={greyArrow} width="14" height="40" alt="" />
              </li>}

            {correctTransport &&
              <li
                className={s.breadcrumbsItem}
                itemScope
                itemType="https://schema.org/ListItem"
                itemProp="itemListElement"
              >
                <Link
                  to={firstLevelLink}
                  className={cx(s.select, s.firstLetterUppercase, 'meta-title-link')}
                  itemProp="url"
                >
                  <span itemProp="title">
                    <FormattedMessage id={transportTitle} />
                    {!isNew &&
                      transport.slug !== 'avto' &&
                      <span>
                        {' '}<FormattedMessage id={'б/у'} />
                      </span>}
                  </span>
                </Link>
                <img className={s.divider} src={greyArrow} width="14" height="40" alt="" />
              </li>}

            {make &&
              <li
                className={s.breadcrumbsItem}
                itemScope
                itemType="https://schema.org/ListItem"
                itemProp="itemListElement"
              >
                <Link
                  title={make.title}
                  to={makeLink}
                  className={cx(s.select, 'meta-title-link-next')}
                  itemProp="url"
                >
                  <span itemProp="title">
                    {make.title}
                  </span>
                </Link>
                <img className={s.divider} src={greyArrow} width="14" height="40" alt="" />
              </li>}

            {model &&
              model.title &&
              <li
                className={s.breadcrumbsItem}
                itemScope
                itemType="https://schema.org/ListItem"
                itemProp="itemListElement"
              >
                <Link
                  title={model.title}
                  to={modelLink}
                  className={cx(s.select, 'meta-title-link-next')}
                  itemProp="url"
                >
                  <span itemProp="title">
                    {model.title}
                  </span>
                </Link>
                <img className={s.divider} src={greyArrow} width="14" height="40" alt="" />
              </li>}

            {year &&
              yearLink &&
              <li
                className={s.breadcrumbsItem}
                itemScope
                itemType="https://schema.org/ListItem"
                itemProp="itemListElement"
              >
                <Link
                  title={year}
                  to={yearLink}
                  className={cx(s.select, 'meta-title-link-next')}
                  itemProp="url"
                >
                  <span itemProp="title">
                    {year}
                  </span>
                </Link>
                <img className={s.divider} src={greyArrow} width="14" height="40" alt="" />
              </li>}

            {location &&
              <li
                className={cx(s.select, s.breadcrumbsItem)}
                itemScope
                itemType="https://schema.org/ListItem"
                itemProp="itemListElement"
              >
                <Link
                  title={location.title}
                  to={locationLink}
                  className={cx(s.select, 'meta-title-link-next')}
                  itemProp="url"
                >
                  <span itemProp="title">
                    <FormattedMessage id={location.title} />
                  </span>
                </Link>
              </li>}
          </div>
          <JsonLdApplication {...breadcrumbsListData} />
          <div className={s.right}>
            {nextItemLink &&
              <Link to={nextItemLink} className={s.next} onClick={this.handleNextAdvertClick}>
                <FormattedMessage id="Следующее объявление" />
                &nbsp;
                <img src={rightArrow} width={12} alt="" />
              </Link>}
          </div>
        </div>
      </div>
    );
  }
}

export default withStyles(s)(
  connect(
    ({ history: { nextSearchItemIndex }, searchForm: { selected: searchFormState } }) => ({
      nextSearchItemIndex,
      searchFormState,
    }),
    dispatch => bindActionCreators({ setNextSearchItemIndex }, dispatch),
  )(SearchBar),
);



// WEBPACK FOOTER //
// ./src/desktop/routes/advert/components/SearchBar/SearchBar.js